import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
	DOCUMENT,
	inject,
	Injectable,
	InjectionToken,
	makeStateKey,
	NgZone,
	PendingTasks,
	PLATFORM_ID,
	RESPONSE_INIT,
	TransferState,
} from '@angular/core';
import { Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';
import type { ISbStoriesParams, ISbStoryData } from '@storyblok/js';
import { useStoryblokBridge } from '@storyblok/js';
import dayjs from 'dayjs';
import type { Response } from 'express';
import {
	catchError,
	filter,
	from,
	map,
	Observable,
	of,
	shareReplay,
	switchMap,
	take,
} from 'rxjs';
import type StoryblokClient from 'storyblok-js-client';

import type { TranslatedSlug } from '@valk-nx/compositions/ui-header/src/lib/header.interface';
import type { Language } from '@valk-nx/core/lib/core';
import { LANGUAGE, languagesLabels } from '@valk-nx/core/lib/core';
import { GeneralHelper } from '@valk-nx/helpers/lib/general/general.helper';
import { RouteHelper } from '@valk-nx/router-store/router.helper';
import { StoryblokHelper } from '@valk-nx/storyblok-helpers/src/lib/general/storyblok.helper';
import {
	HOST,
	MetadataService,
} from '@valk-nx/storyblok-services/src/lib/services/metadata.service';
import { StoryblokParamsService } from '@valk-nx/storyblok-services/src/lib/services/storyblok-params.service';
import type {
	Banner,
	Footer,
	ISbComponent,
	ISbStoryLinks,
	PageMetaData,
	Redirect,
	SbFooterMenu,
	StoryblokGlobals,
} from '@valk-nx/storyblok-types/src/lib/types/storyblok.interface';

import { SB_CLIENT_FACTORY } from './storyblok.client';
import { StoryblokCacheVersionService } from './storyblok-cache-version.service';

export const STORYBLOK_ACCESS_TOKEN = new InjectionToken<string>(
	'storyblok-access-token',
);

export const STORYBLOK_ENDPOINT = new InjectionToken<string>(
	'storyblok-endpoint',
);

export const USE_TRANSFER_STATE = new InjectionToken<boolean>(
	'storyblok-transferstate',
);

export const RESPONSE = new InjectionToken<Response>('response');

@Injectable({
	providedIn: 'root',
})
export class StoryblokService {
	private readonly token = inject(STORYBLOK_ACCESS_TOKEN);
	private readonly useTransferState = inject(USE_TRANSFER_STATE);
	private readonly storyblokClientFactoryFunction = inject(SB_CLIENT_FACTORY);

	private readonly endpoint = inject(STORYBLOK_ENDPOINT, { optional: true });
	private readonly metadataService = inject(MetadataService);
	private readonly sbCacheVersionService = inject(
		StoryblokCacheVersionService,
	);
	private readonly router = inject(Router);
	private readonly ngZone = inject(NgZone);
	private readonly document = inject<Document>(DOCUMENT);
	platformId = inject(PLATFORM_ID);
	private readonly response = inject<ResponseInit>(RESPONSE_INIT);
	private readonly pendingTasks = inject(PendingTasks);
	private readonly transferState = inject(TransferState);
	protected hostUrl = inject(HOST);
	protected language = inject<Language>(LANGUAGE);

	storyblokBridge = useStoryblokBridge;
	public sbClient: StoryblokClient | undefined;

	public readonly translatedSlugs$: Observable<TranslatedSlug[]>;
	public readonly story$: Observable<ISbStoryData>;
	public readonly storyByUUID$: (uuid: string) => Observable<ISbStoryData>;
	public readonly globals$: Observable<StoryblokGlobals>;
	private readonly storyblokParamsService = inject(StoryblokParamsService);
	private readonly translateService = inject(TranslateService);

	constructor() {
		this.sbClient = this.storyblokClientFactoryFunction(
			this.token,
			this.endpoint,
		);

		this.story$ = this.storyblokParamsService.storyblokParams$.pipe(
			// Workaround will be fixed in VPL-48435
			// Wait for the translation file before emitting the story
			switchMap((params) =>
				this.translateService.use(params.language).pipe(
					take(1),
					catchError(() => of(null)),
					map(() => params),
				),
			),
			switchMap(({ slug, isDraft, language }) => {
				dayjs.locale(language);
				this.document.documentElement.lang = language;

				const transferKey = `${this.token}/${language}/${slug}`;
				const cachedStory =
					this.readFromTransferState<ISbStoryData>(transferKey);
				if (cachedStory) {
					return of(cachedStory);
				}

				return from(
					this.getStory(slug, isDraft, language).catch(() => {
						this.handleStoryNotFound(slug, language);

						return this.getStory('error-404', isDraft, language);
					}),
				).pipe(
					switchMap((storyblokData) => {
						// NOTE: first map correct languages from links to url
						let data = StoryblokHelper.mapLanguageUrlSlug(
							storyblokData.data.story,
							storyblokData.data.links,
							language,
							'link',
							this.language,
						);
						// NOTE: replace all storyblok urls with the correct host
						data = StoryblokHelper.replaceStoryblokUrls(
							data,
							this.hostUrl,
						);

						const content =
							data.content as unknown as PageMetaData & {
								component: string;
							};

						if (content.metaTitle) {
							const metaData = {
								...content,
								robotsNoFollow:
									content.component === 'menulessPage'
										? true
										: content.robotsNoFollow,
								robotsNoIndex:
									content.component === 'menulessPage'
										? true
										: content.robotsNoIndex,
							};

							this.metadataService.updateMetadata(metaData);
						}

						this.metadataService.createLinkForCanonicalURL(
							data.content['canonical'],
						);

						this.writeToTransferState(transferKey, data);

						return new Observable<ISbStoryData>((observer) => {
							observer.next(data);
							if (
								data &&
								isDraft &&
								isPlatformBrowser(this.platformId)
							) {
								/* v8 ignore start */
								// Listen to Storyblok Visual Editor events https://github.com/storyblok/storyblok-js#2-listen-to-storyblok-visual-editor-events
								this.storyblokBridge(
									data.id,
									(bridgeData) => {
										observer.next(bridgeData);

										// Scroll to the target inside the Visual Editor after changes have been 'pushed'
										setTimeout(() => {
											GeneralHelper.scrollToElementWithId(
												'storyblok__overlay',
												200,
												false,
											);
										}, 100);
									},
									{ preventClicks: true },
								);
								/* v8 ignore stop */
							}
						});
					}),
				);
			}),
			shareReplay(1),
		);

		this.translatedSlugs$ = this.story$.pipe(
			map((storyblokData) =>
				this.mapStoryToTranslatedSlugs(storyblokData),
			),
		);

		this.globals$ = this.storyblokParamsService.storyblokParams$.pipe(
			switchMap(({ isDraft, language }) => {
				const transferKey = `${this.token}/${language}/globals`;
				const cachedGlobals =
					this.readFromTransferState<StoryblokGlobals>(transferKey);
				if (cachedGlobals) {
					return of(cachedGlobals);
				}
				return from(this.getStory('globals', isDraft, language)).pipe(
					map((storyblokData) => {
						const { content } = storyblokData.data.story;

						// NOTE: replace all storyblok urls with the correct host
						let storyData = StoryblokHelper.replaceStoryblokUrls(
							content['content'],
							this.hostUrl,
						) as unknown as ISbComponent[];

						storyData = StoryblokHelper.mapLanguageUrlSlug(
							storyData as unknown as ISbStoryData,
							storyblokData.data.links,
							language,
							'link',
							this.language,
						) as unknown as ISbComponent[];

						const bannerData = content[
							'banner'
						] as unknown as Banner[];

						const banner = bannerData?.find(
							(x) => x.component === 'banner',
						);

						const header = storyData.find(
							(x) => x.component === 'header',
						) as StoryblokGlobals['header'];

						const footer = storyData.find(
							(x) => x.component === 'footer',
						) as ISbComponent & Footer;
						const footerMenu = StoryblokHelper.mapFooterMenu(
							footer?.menu as unknown as SbFooterMenu[],
						);
						const redirects = content?.['redirects'] || null;

						const data: StoryblokGlobals = {
							header,
							banner,
							footer: { ...footer, footerMenu },
							redirects,
							language: this.language,
						};

						// The redirect table (thousands of entries on some
						// sites) is only read on the 404 path; don't ship it
						// to the browser.
						this.writeToTransferState(transferKey, data, () => ({
							...data,
							redirects: null,
						}));

						return data;
					}),
					shareReplay(1),
				);
			}),
		);

		this.storyByUUID$ = (uuid: string) =>
			this.storyblokParamsService.storyblokParams$.pipe(
				switchMap(({ isDraft, language }) => {
					const transferKey = `${this.token}/${language}/${uuid}`;
					const cachedStory =
						this.readFromTransferState<ISbStoryData>(transferKey);
					if (cachedStory) {
						return of(cachedStory);
					}
					return from(
						this.getStory(uuid, isDraft, language, {
							find_by: 'uuid',
						}),
					).pipe(
						map((storyblokData) => {
							let data = StoryblokHelper.mapLanguageUrlSlug(
								storyblokData.data.story,
								storyblokData.data.links,
								language,
								'link',
								this.language,
							);
							// NOTE: replace all storyblok urls with the correct host
							data = StoryblokHelper.replaceStoryblokUrls(
								data,
								this.hostUrl,
							);

							this.writeToTransferState(transferKey, data);

							return data;
						}),
					);
				}),
			);
	}

	/**
	 * Emits the current story's translated slugs.
	 * Only for page components rendered by SbRootComponent. Global components
	 * (header, wrapper) must use `translatedSlugs$`.
	 */
	translatedSlugsForCurrentStory(): Observable<TranslatedSlug[]> {
		return this.story$.pipe(
			take(1),
			switchMap((currentStory) =>
				this.story$.pipe(
					filter(
						(storyblokData) =>
							storyblokData.content['component'] ===
							currentStory.content['component'],
					),
				),
			),
			map((storyblokData) =>
				this.mapStoryToTranslatedSlugs(storyblokData),
			),
		);
	}

	private mapStoryToTranslatedSlugs(
		storyblokData: ISbStoryData,
	): TranslatedSlug[] {
		const currentLang = storyblokData.lang;

		const defaultLanguageMenuItem = {
			path: `${storyblokData['default_full_slug']}`.replace(/\/$/, ''),
			name: languagesLabels[this.language.toLowerCase() as Language],
			lang: this.language.toUpperCase() as Language,
			selected: this.language === currentLang,
		};
		const languageMenuItems =
			storyblokData['translated_slugs']?.map((slug) => {
				return {
					path: `${slug.lang}/${slug.path}`.replace(/\/$/, ''),
					name: languagesLabels[slug.lang.toLowerCase() as Language],
					lang: slug.lang.toUpperCase() as Language,
					selected: slug.lang === currentLang,
				};
			}) ?? [];

		const allSlugs = [defaultLanguageMenuItem, ...languageMenuItems];

		this.metadataService.setHrefLang(allSlugs, this.language);

		return allSlugs;
	}

	/**
	 * Reads a previously transferred value from the TransferState.
	 *
	 * @param oneShot when `true`, the value is removed from the store after the
	 * browser reads it once, so later in-app navigations fetch fresh data
	 * instead of replaying the server-rendered response.
	 */
	private readFromTransferState<T>(key: string, oneShot = false): T | null {
		if (!this.useTransferState) {
			return null;
		}

		const stateKey = makeStateKey<string>(key);
		const stored = this.transferState.get(stateKey, null);
		if (!stored) {
			return null;
		}

		if (oneShot && isPlatformBrowser(this.platformId)) {
			this.transferState.remove(stateKey);
		}

		return JSON.parse(stored) as T;
	}

	/**
	 * Persists a value in the TransferState. Only the server writes: the value
	 * is serialized into the document and read by the browser during hydration.
	 * Writing on the browser is pointless and would defeat one-shot handoffs.
	 *
	 * @param toBrowserValue optional hook that replaces the value in the
	 * payload serialized to the browser; server-side reads keep `value`.
	 */
	private writeToTransferState<T>(
		key: string,
		value: T,
		toBrowserValue?: () => T,
	): void {
		if (this.useTransferState && isPlatformServer(this.platformId)) {
			const stateKey = makeStateKey<string>(key);
			this.transferState.set(stateKey, JSON.stringify(value));
			if (toBrowserValue) {
				this.transferState.onSerialize(stateKey, () =>
					JSON.stringify(toBrowserValue()),
				);
			}
		}
	}

	/**
	 * Builds a stable, query-specific TransferState key. Keys are sorted so the
	 * server and browser produce identical keys regardless of property order.
	 */
	private buildQueryTransferKey(
		query: Partial<ISbStoriesParams>,
		language: Language,
	): string {
		const serializedQuery = Object.keys(query)
			.sort((a, b) => a.localeCompare(b))
			.map(
				(key) =>
					`${key}=${JSON.stringify(query[key as keyof ISbStoriesParams])}`,
			)
			.join('&');

		return `${this.token}/${language}/query?${serializedQuery}`;
	}

	handleStoryNotFound(slug: string, language: Language = 'nl'): void {
		const slugToMatch =
			language === this.language ? slug : `${language}/${slug}`;

		this.globals$.pipe(take(1)).subscribe((sbData) => {
			const redirect: Redirect | undefined = sbData.redirects?.find(
				(redirect) =>
					redirect.fromUrl.toLowerCase() ===
					`/${slugToMatch.toLowerCase()}`,
			);

			if (redirect) {
				/* v8 ignore start */
				if (isPlatformServer(this.platformId)) {
					this.response.headers = new Headers(this.response.headers);
					this.response.headers.set('location', redirect.toUrl);
					this.response.status = +redirect.status;
				} else if (isPlatformBrowser(this.platformId)) {
					if (
						redirect.toUrl.startsWith('http') ||
						redirect.toUrl.startsWith('www')
					) {
						RouteHelper.replaceLocation(redirect.toUrl);
					} else {
						this.router.navigateByUrl(redirect.toUrl, {
							replaceUrl: true,
						});
					}
				}
				/* v8 ignore stop */
			} else {
				// Set the document status to 404 because from here the error-404 page will be served
				/* v8 ignore start */
				if (isPlatformServer(this.platformId) && this.response) {
					const MAX_AGE = 86400;

					this.response.status = 404;
					this.response.headers = new Headers(this.response.headers);

					this.response.headers.set(
						'cache-control',
						`public, max-age=${MAX_AGE}, s-maxage=${MAX_AGE}`,
					);
				}
				/* v8 ignore stop */
			}
		});
	}

	async getStory(
		slug: string,
		isDraft: boolean,
		language: Language = this.language,
		params?: Record<string, string>,
	): Promise<ISbStoryLinks> {
		// this forces the SSR to wait until the task is done, as the
		// storyblok sdk doesn't use the Angular HttpClient
		const taskDone = this.pendingTasks.add();
		try {
			// Run outside the Angular zone: the Storyblok client schedules a
			// trailing 1s setTimeout per request to drain its throttle queue,
			// which would otherwise keep the zone unstable and delay SSR
			// serialization. PendingTasks above already holds stability for
			// exactly the duration of the request itself.
			return await this.ngZone.runOutsideAngular(async () => {
				const cv = await this.sbCacheVersionService.ensureCv(
					this.sbClient as StoryblokClient,
					this.token,
					this.useTransferState,
				);
				return await (this.sbClient as StoryblokClient).get(
					`cdn/stories/${slug}`,
					{
						...params,
						resolve_links: 'link',
						version: isDraft ? 'draft' : 'published',
						language,
						cv,
					},
				);
			});
		} finally {
			taskDone();
		}
	}

	async getStoriesByQuery(
		query: Partial<ISbStoriesParams>,
		language: Language = this.language,
	): Promise<ISbStoryLinks> {
		const transferKey = this.buildQueryTransferKey(query, language);
		const cached = this.readFromTransferState<ISbStoryLinks>(
			transferKey,
			true,
		);
		if (cached) {
			return cached;
		}

		// this forces the SSR to wait until the task is done, as the
		// storyblok sdk doesn't use the Angular HttpClient
		const taskDone = this.pendingTasks.add();
		try {
			// Outside the Angular zone for the same reason as in getStory.
			const response = await this.ngZone.runOutsideAngular(async () => {
				const cv = await this.sbCacheVersionService.ensureCv(
					this.sbClient as StoryblokClient,
					this.token,
					this.useTransferState,
				);
				return await (this.sbClient as StoryblokClient).get(
					`cdn/stories`,
					{
						...query,
						resolve_links: 'link',
						version: 'published',
						language,
						cv,
					},
				);
			});
			this.writeToTransferState(transferKey, response);

			return response;
		} finally {
			taskDone();
		}
	}
}
